import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { FC, useEffect, useState } from 'react'
import { LoggedOutGeneratorSection } from '../../LoggedOutCodeGenerator.types'
import { RootState } from '@dtx-company/inter-app/src/redux/types'
import {
  selectProgress,
  selectScanDestinationPresent,
  selectSection
} from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useProgressBarDesktopSections } from './hooks/useProgressBarDesktopSections'
import { useSelector as useReduxSelector } from 'react-redux'
import { useSelector } from '@xstate/react'

interface ProgressBarSegmentProps {
  active: boolean
  width: number
  displayBorder: boolean
  label?: string
  onClick?: () => void
  hoverLabel?: string
  disabled?: boolean
}

const SECTION_LABEL_HEIGHT = 4
const PROGRESS_BAR_LABEL_CX = 'progress-bar-label'

const ProgressBarSegment: FC<ProgressBarSegmentProps> = props => {
  return (
    <Box
      data-testid={props.label}
      sx={theme => ({
        backgroundColor: props.active ? 'tertiary.main' : 'surface.4',
        height: theme.spacing(1.5),
        position: 'relative',
        flex: props.width,
        mx: 0,
        p: 0,
        border: 'none',
        my: theme.spacing(SECTION_LABEL_HEIGHT),
        [`.${PROGRESS_BAR_LABEL_CX}`]: {
          lineHeight: 'normal',
          position: 'absolute',
          top: '100%',
          left: 0,
          opacity: 0
        },
        ':focus-within, :hover': {
          [`.${PROGRESS_BAR_LABEL_CX}`]: {
            opacity: 1
          }
        },
        ':after': {
          ...(props.label && { content: `'${props.label}'`, pb: 1.5 }),
          position: 'absolute',
          whiteSpace: 'nowrap',
          left: 0,
          bottom: '100%',
          fontWeight: 500,
          color: props.active ? theme.palette.text.primary : theme.palette.text.secondary,
          fontSize: theme.spacing(SECTION_LABEL_HEIGHT),
          lineHeight: theme.spacing(SECTION_LABEL_HEIGHT)
        },
        ...(props.onClick && {
          cursor: 'pointer'
        })
      })}
      {...(props.onClick && {
        onClick: props.onClick,
        component: 'button',
        'data-heap-id': 'LoggedOutGenerator_ProgressBarSegment',
        'data-section': props.hoverLabel || props.label,
        disabled: props.disabled
      })}
    >
      {props.hoverLabel && (
        <Typography variant={'labelSmall'} className={PROGRESS_BAR_LABEL_CX} color="text.secondary">
          {props.hoverLabel}
        </Typography>
      )}
    </Box>
  )
}

export const ProgressBarDesktop: FC = () => {
  const { locgService } = useGlobalServices()
  const section = useSelector(locgService, selectSection)
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)
  const scanDestinationPresent = useSelector(locgService, selectScanDestinationPresent)
  const progress = useSelector(locgService, selectProgress)
  const sections = useProgressBarDesktopSections()
  // TODO - update to pull this field from the authMachine - DF
  const disabled = useReduxSelector(
    (state: RootState) => state.appReducer.authFormReducer.submitting
  )

  // keep highest index when user scrolls to the top
  useEffect(() => {
    setActiveSectionIndex(activeIndex =>
      Math.max(
        sections.findIndex(el => el.id === section),
        activeIndex,
        0
      )
    )
  }, [section, sections])

  return (
    <Box
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress * 100}
      sx={{
        display: {
          xs: 'none',
          md: 'block'
        },
        my: 4,
        px: { md: 8.5, xl: 0 }
      }}
    >
      <Stack
        direction={'row'}
        sx={theme => ({
          px: theme.spacing(4)
        })}
        gap={2}
      >
        {sections.map((section, index) => {
          const hasSectionBeenVisited = activeSectionIndex > index
          return (
            <ProgressBarSegment
              key={section.id}
              width={section.width}
              active={
                hasSectionBeenVisited ||
                (activeSectionIndex >= index &&
                  (section.id != LoggedOutGeneratorSection.ScanDestination ||
                    scanDestinationPresent))
              }
              disabled={disabled}
              displayBorder={index !== 0}
              label={section.label}
              hoverLabel={section.hoverLabel}
              onClick={section.onClick}
            />
          )
        })}
      </Stack>
    </Box>
  )
}
